import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export interface UserProfile {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  aura_points: number | null;
  aura_level: string | null;
  style_personality: string | null;
  is_premium: boolean | null;
}

export interface FollowStats {
  followers: number;
  following: number;
}

export function useFollow() {
  const { user } = useAuth();
  const [following, setFollowing] = useState<string[]>([]);
  const [followers, setFollowers] = useState<string[]>([]);
  const [suggestedUsers, setSuggestedUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchFollowData = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      // Fetch who user is following
      const { data: followingData } = await supabase
        .from('user_follows')
        .select('following_id')
        .eq('follower_id', user.id);

      const followingIds = followingData?.map(f => f.following_id) || [];
      setFollowing(followingIds);

      // Fetch user's followers
      const { data: followersData } = await supabase
        .from('user_follows')
        .select('follower_id')
        .eq('following_id', user.id);

      const followerIds = followersData?.map(f => f.follower_id) || [];
      setFollowers(followerIds);

      // Fetch suggested users (people not followed, with high aura) using public_profiles view
      const { data: suggestedData } = await supabase
        .from('public_profiles' as any)
        .select('id, display_name, avatar_url, aura_points, aura_level')
        .not('id', 'eq', user.id)
        .order('aura_points', { ascending: false })
        .limit(20) as { data: Array<{ id: string; display_name: string | null; avatar_url: string | null; aura_points: number | null; aura_level: string | null }> | null };

      // Filter out already followed users
      const notFollowed = (suggestedData || []).filter(u => !followingIds.includes(u.id));
      setSuggestedUsers(notFollowed.slice(0, 10).map(u => ({
        ...u,
        style_personality: null,
        is_premium: null
      })));
    } catch (error) {
      console.error('Error fetching follow data:', error);
    }

    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchFollowData();
  }, [fetchFollowData]);

  const followUser = async (userId: string) => {
    if (!user) {
      toast.error('Please sign in to follow');
      return false;
    }

    if (userId === user.id) {
      toast.error("You can't follow yourself");
      return false;
    }

    try {
      const { error } = await supabase
        .from('user_follows')
        .insert({ follower_id: user.id, following_id: userId });

      if (error) throw error;

      setFollowing(prev => [...prev, userId]);
      setSuggestedUsers(prev => prev.filter(u => u.id !== userId));

      // Send notification via secure edge function
      await supabase.functions.invoke('send-notification', {
        body: {
          target_user_id: userId,
          type: 'new_follower',
          title: 'New Follower',
          message: 'Someone started following you!',
          data: { follower_id: user.id },
        },
      });

      toast.success('Following!');
      return true;
    } catch (error) {
      console.error('Error following user:', error);
      toast.error('Failed to follow');
      return false;
    }
  };

  const unfollowUser = async (userId: string) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('user_follows')
        .delete()
        .eq('follower_id', user.id)
        .eq('following_id', userId);

      if (error) throw error;

      setFollowing(prev => prev.filter(id => id !== userId));
      toast.success('Unfollowed');
      return true;
    } catch (error) {
      console.error('Error unfollowing user:', error);
      toast.error('Failed to unfollow');
      return false;
    }
  };

  const isFollowing = (userId: string) => following.includes(userId);

  const getFollowStats = async (userId: string): Promise<FollowStats> => {
    const { data: followersData } = await supabase
      .from('user_follows')
      .select('id')
      .eq('following_id', userId);

    const { data: followingData } = await supabase
      .from('user_follows')
      .select('id')
      .eq('follower_id', userId);

    return {
      followers: followersData?.length || 0,
      following: followingData?.length || 0,
    };
  };

  return {
    following,
    followers,
    suggestedUsers,
    loading,
    followUser,
    unfollowUser,
    isFollowing,
    getFollowStats,
    refetch: fetchFollowData,
  };
}
